import { useCallback, useState } from 'react'
import { isPageElement, SketchElement } from '../../../../../inspector'
import {
  useCanvasClickHandler,
  useCanvasMouseMoveHandler,
} from '../sketchSceneLib'
import { isUserInteractionEnabledOnSketchElement } from './isUserInteractionEnabledOnSketchElement'

/**
 * Create event handler and react states to display hover and selected elements state.
 */
export function useInspectorOverlayEvents(
  sketchSceneRootElement: SketchElement | null,
  /** This should only be enabled when the inspector is open and ready */
  isEnabled: boolean
) {
  const [selectedElement, setSelectedElement] = useState<SketchElement | null>(
    null
  )

  const [hoveredElement, setHoveredElement] = useState<SketchElement | null>(
    null
  )

  const guardedSetSelectedElement = useCallback(
    (selectedElement: SketchElement | null) => {
      if (selectedElement && isPageElement(selectedElement)) {
        setSelectedElement(null)
        // We don't allow to select the page element
        return
      }
      setSelectedElement(selectedElement)
    },
    []
  )

  const guardedSetHoveredElement = useCallback(
    (hoveredElement: SketchElement | null) => {
      if (hoveredElement && isPageElement(hoveredElement)) {
        setHoveredElement(null)
        // We don't allow to hover the page element
        return
      }
      setHoveredElement(hoveredElement)
    },
    []
  )

  const { clickHandler, mouseDownHandle } = useCanvasClickHandler(
    sketchSceneRootElement,
    guardedSetSelectedElement,
    isUserInteractionEnabledOnSketchElement
  )

  const mouseMoveHandler = useCanvasMouseMoveHandler(
    isEnabled,
    sketchSceneRootElement,
    guardedSetHoveredElement,
    isUserInteractionEnabledOnSketchElement
  )

  const setSelectedElementExternal = useCallback(
    (selectedElement: SketchElement | null) => {
      guardedSetSelectedElement(selectedElement)
    },
    [guardedSetSelectedElement]
  )

  const setHoveredElementExternal = useCallback(
    (selectedElement: SketchElement | null) => {
      guardedSetHoveredElement(selectedElement)
    },
    [guardedSetHoveredElement]
  )

  return {
    hoveredElement,
    selectedElement,
    /**
     * We need to also be able to set the selected element identifier from outside of events.
     * (using context menu for examples)
     */
    setSelectedElement: setSelectedElementExternal,
    setHoveredElement: setHoveredElementExternal,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  }
}
